import { useEffect, useRef } from 'react';
import * as intersectsOperator from '@arcgis/core/geometry/operators/intersectsOperator';
import { setDisableMapClick } from '@origo/map-context';
import { useAppDispatch } from '../../../store/storeHooks';

/**
 * Enkel kartvelger for delområde: når brukeren står på en delområdetype-
 * kontekst kan de klikke i kartet for å velge det delområdet klikket treffer.
 * Løser problemet med at mange delområder deler samme navn og er umulige å
 * skille i listen.
 *
 * Speiler klikk→hitTest→velg-mønsteret fra `useDelområdeMapSelection` (gruppe-
 * editoren), men er bevisst minimal: ingen rektangel-tegning, ingen
 * blokkerte-medlemmer, ingen egne markerings-lag. Treff testes in-memory mot
 * polygonene vi allerede har på hvert delområde (`Område.geometry`), og ved
 * overlapp velges det minste (mest spesifikke) delområdet — samme regel som
 * gruppe-editoren.
 *
 * Delområdene hentes i kartets SR (se useAdminAreas/outputWkid), så vi
 * reprojiserer ikke her — `intersectsOperator` krever lik SR og får det.
 *
 * Selve markeringen i kartet håndteres av `AdminPage.highlightContext`, som
 * kjører automatisk via `onPick` (handleSelect). Hooken disabler appens
 * standard kart-klikk (popup/lokasjon) mens den er aktiv, på samme måte som
 * gruppe-editoren.
 */

export interface DelomradeMapPickCandidate {
    id: string;
    navn: string;
    geometry?: __esri.Polygon;
}

export interface UseDelomradeContextMapPickProps {
    /** Live MapView/SceneView fra useMapViewContext(). null til kartet er klart. */
    mapView: __esri.MapView | __esri.SceneView | null | undefined;
    /** Hovedbryter — klikk-handleren kobles kun på når dette er sant. */
    enabled: boolean;
    /** Delområdene i den aktive typen, med geometri. */
    candidates: readonly DelomradeMapPickCandidate[];
    /** Kalles med det valgte delområdet når et klikk treffer. */
    onPick: (id: string, navn: string) => void;
}

const extentArea = (g?: __esri.Polygon): number => {
    const e = g?.extent;
    if (!e) return Number.POSITIVE_INFINITY;
    return e.width * e.height;
};

export function useDelomradeContextMapPick({
    mapView,
    enabled,
    candidates,
    onPick,
}: UseDelomradeContextMapPickProps): void {
    const dispatch = useAppDispatch();

    const clickHandleRef = useRef<IHandle | null>(null);
    const onPickRef = useRef(onPick);
    useEffect(() => { onPickRef.current = onPick; }, [onPick]);

    // Kandidatene holdes i en ref så klikk-handleren leser ferske verdier uten
    // å re-registrere seg. Geometriene er allerede i kartets SR (hentet med
    // outputWkid), så ingen reprojisering trengs.
    const candidatesRef = useRef(candidates);
    useEffect(() => { candidatesRef.current = candidates; }, [candidates]);

    useEffect(() => {
        if (!enabled || !mapView) return;
        if (clickHandleRef.current) return;

        // Demp appens standard kart-klikk (popup/lokasjon) mens velgeren er aktiv.
        dispatch(setDisableMapClick(true));

        clickHandleRef.current = mapView.on('click', (event: __esri.ViewClickEvent) => {
            const point = event.mapPoint;
            if (!point) return;

            // Treff blant delområdene; ved overlapp velg minste (mest spesifikke).
            const hits = candidatesRef.current
                .filter((c) => c.geometry && intersectsOperator.execute(point, c.geometry));
            if (hits.length === 0) return;
            hits.sort((a, b) => extentArea(a.geometry) - extentArea(b.geometry));

            onPickRef.current(hits[0].id, hits[0].navn);
        });

        return () => {
            clickHandleRef.current?.remove();
            clickHandleRef.current = null;
            dispatch(setDisableMapClick(false));
        };
    }, [enabled, mapView, dispatch]);
}
